import { Box, Typography, IconButton, Grid, Hidden, LinearProgress } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { useHistory } from 'react-router';
import { Formik } from 'formik';
import { MFSubmitButton, MFTextField } from '../../lib/formik';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import {
  DistributorListType,
  RMType,
  SRMType,
  addSrmDetails,
  mappedUserMasterType,
} from '../../redux-store/types/api-types';
import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import { Location } from 'history';
import { addSRM, editSRM, getSRMById } from '../../redux-store/actions/userManagement';
import { userManagementSRMSchema } from '../../utils/schema';
import { nonDigitRegex } from '../../utils/regex';
import { MultipleSelect } from '../../lib/formik/MultipleSelectField';
import { getDistributorsListForFilters, getRMsList } from '../../redux-store/actions';
import { srmMappedUsersMaster, YesNoMaster } from '../../utils/constant';

const initialValues: addSrmDetails = {
  name: '',
  email: '',
  countryNameAndCode: 'India: +91',
  countryCode: '+91',
  phone: '',
  mappedUsersId: null,
  mappedUsersIdAndRole: null,
  isActive: true,
};

export default function InviteSrmDetails({
  location,
}: {
  location: Location<{ srmId: number }>;
}): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();
  const { srmId } = location.state || { srmId: '' };
  const [srmDetails, setSrmDetails] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const [mappedusersList, setMappedusersList] = useState<mappedUserMasterType[]>([]);

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        setLoading(true);
        const distributorsList = (await dispatch(
          getDistributorsListForFilters({ isActive: true })
        )) as unknown as DistributorListType[];

        const rmsList = (await dispatch(getRMsList({ isActive: true }))) as unknown as RMType[];

        const mappedDistributorsList = distributorsList?.map((distributor) => {
          return {
            name: distributor?.name,
            id: distributor?.id,
            role: 'distributor',
            type: distributor?.type,
          };
        }) as mappedUserMasterType[];

        const mappedRmList = rmsList?.map((rm) => {
          return {
            name: rm?.name,
            id: rm?.id,
            role: 'rm',
          };
        }) as mappedUserMasterType[];

        setMappedusersList([...mappedRmList, ...mappedDistributorsList]);
        if (srmId) {
          const getSRmDetails = (await dispatch(getSRMById(srmId))) as unknown as SRMType;
          const { name, id, isActive, mappedUsers } = getSRmDetails;
          const { phone, email } = getSRmDetails.user;
          console.log(mappedUsers);
          setSrmDetails({
            ...srmDetails,
            name,
            id,
            phone,
            email,
            isActive,
            mappedUsersId: mappedUsers?.map((_user) => {
              return `${_user?.id}-${_user?.type ? 'distributor' : 'rm'}`;
            }),
          });
        }
        if (!isComponentAlive) return;
        setLoading(false);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (isComponentAlive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isComponentAlive = false;
    };
  }, []);

  const onSubmit = async (values: addSrmDetails) => {
    const { mappedUsersId, ...rest } = values;
    const mappedUsersIdAndRole = mappedUsersId?.map((_user: string) => {
      const idAndRole = _user?.split('-');
      return {
        mappedUserRole: idAndRole[1],
        mappedUserRoleId: parseInt(idAndRole[0]),
      };
    });
    try {
      if (!srmId) {
        await dispatch(
          addSRM({
            ...rest,
            countryCode: rest.countryNameAndCode?.split(':')[1].trim(),
            mappedUsersIdAndRole,
          })
        );
        history.push(`service-managers`);
      } else {
        await dispatch(
          editSRM(srmId, {
            ...rest,
            countryCode: rest.countryNameAndCode?.split(':')[1].trim(),
            mappedUsersIdAndRole,
          })
        );
        history.push(`service-managers-details`, { srmId });
      }
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  return (
    <>
      <Formik
        initialValues={srmDetails}
        onSubmit={onSubmit}
        enableReinitialize={true}
        validationSchema={userManagementSRMSchema}>
        {({ handleSubmit }) => (
          <Box
            sx={{
              bgcolor: 'white',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
              borderRadius: '10px',
              py: { xs: 2, sm: 5 },
              pl: { xs: 0, sm: 5 },
              mt: { xs: 2, sm: 5 },
            }}
            component="form"
            noValidate
            onSubmit={handleSubmit}>
            <Hidden smUp={true}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  sx={{ height: 'fit-content', p: 0 }}
                  onClick={() =>
                    srmId
                      ? history.push(`service-managers-details`, { srmId })
                      : history.push('service-managers')
                  }>
                  <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                </IconButton>
                <Typography
                  component="h3"
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    color: '#1C2D47',
                  }}>
                  Back
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  my: 2,
                  py: 2,
                  pl: { xs: 3, sm: 0 },
                  color: 'primary.main',
                  bgcolor: 'rgba(238, 244, 251, 0.5)',
                }}>
                <PersonOutlineOutlinedIcon fontSize="large" />
                <Typography
                  component="h3"
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    pl: 1,
                  }}>
                  User Management
                </Typography>
              </Box>
            </Hidden>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Hidden only="xs">
                <IconButton
                  sx={{ height: 'fit-content', p: 0 }}
                  onClick={() =>
                    srmId
                      ? history.push(`service-managers-details`, { srmId })
                      : history.push('service-managers')
                  }>
                  <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                </IconButton>
              </Hidden>
              <Typography
                component="h3"
                sx={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: '#1C2D47',
                  pl: { xs: 2, sm: 0 },
                }}>
                {srmId ? 'SRM Details' : 'Onboarding SRM Details'}
              </Typography>
            </Box>

            {loading ? (
              <LinearProgress />
            ) : (
              <>
                <Grid container rowSpacing={1} columnSpacing={4} px={4} pt={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField name="name" label="SRM Name *" placeholder="Enter SRM Name" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="email"
                      label="SRM Email Address *"
                      placeholder="Enter SRM Email Address"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="phone"
                      label="SRM Mobile *"
                      placeholder="Enter SRM Mobile"
                      regexForFilterValue={nonDigitRegex}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12}>
                    <MultipleSelect
                      name="mappedUsersId"
                      label="Mapped user *"
                      items={mappedusersList?.map((item: mappedUserMasterType) => ({
                        key: `${item?.id}-${item?.role !== 'rm' ? 'distributor' : 'rm'}` as string,
                        value: `${item.name} (${item.type ? item.type + ' ' : ''}${
                          srmMappedUsersMaster[item.role || '']
                        })`,
                      }))}
                      renderText="Selected mapped user"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ width: '100%', maxWidth: '350px', mx: 'auto', mt: 3 }}>
                  <MFSubmitButton label="Save" />
                </Box>
              </>
            )}
          </Box>
        )}
      </Formik>
    </>
  );
}
